/**
 * Atalhos globais (§5.7, §15.2.7): UM ouvinte de `keydown` em `window`, fase de bolha. Nunca trata Esc (é de
 * a11y/escape.ts, que para a propagação quando age). As ações e o leitor da chave `atalhos-uma-tecla` são injetados
 * pelo App (DE-B17: `a11y/` não importa state/actions.ts nem state/storage.ts); o estado (`ui`) vem de state/store.ts.
 */
import { ui } from '../state/store';

const NON_TEXT_INPUTS: ReadonlySet<string> = new Set(['button', 'checkbox', 'radio', 'range', 'submit', 'reset']);

/** Campo de digitação (§5.7.1): textarea, select, input de texto e conteúdo editável. */
export function isTypingTarget(el: EventTarget | null): boolean {
  if (!el || typeof (el as Element).tagName !== 'string') return false;
  const e = el as HTMLElement;
  const tag = e.tagName.toUpperCase();
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag === 'INPUT') return !NON_TEXT_INPUTS.has(((e as HTMLInputElement).type || 'text').toLowerCase());
  if (e.isContentEditable === true) return true;
  return typeof e.closest === 'function' && e.closest('[contenteditable]:not([contenteditable="false"])') !== null;
}

/** §15.2.7: nenhum atalho de uma tecla com o foco num `role="slider"` ou dentro de um `listbox` aberto. */
function inSliderOrListbox(el: EventTarget | null): boolean {
  const e = el as Element | null;
  return !!e && typeof e.closest === 'function' && e.closest('[role="slider"],[role="listbox"]') !== null;
}

export type ZoomAction = 'in' | 'out' | 'reset';

/** O que o App injeta (DE-B17). `O` = `RouteOffice` no App. */
export interface ShortcutHandlers<O extends string = string> {
  /** `toggleSearch(open?)` de state/actions.ts: sem argumento alterna (Ctrl/⌘+K); `true` abre (`/`). */
  toggleSearch: (open?: boolean) => void;
  /** Abas disponíveis na ordem fixa (`availableOffices(ctx)`, lido pelo computed `offices`). */
  offices: () => readonly O[];
  /** `setOffice(office)` de state/actions.ts. */
  setOffice: (office: O) => void;
  /** Zoom do mapa (§9.8). Devolve false quando o `ElectionMap` não está montado (não agiu). O App liga `mapZoom` (features/map/mapLink.ts). */
  zoom?: (action: ZoomAction) => boolean;
  /** `C`: alterna Apuração e Comentários (ORD-F-026). Só onde a tela existe; devolve false quando não agiu. */
  toggleScreen?: () => boolean;
  /** Chave X1 (§15.2.7): `getOneKeyShortcuts()` de state/actions.ts (ligado por padrão). */
  oneKeyEnabled: () => boolean;
}

/**
 * Trata uma tecla (exportado para testes). Devolve true se agiu; só então chama `preventDefault` (AC-5.4-05).
 * Ordem de §5.7.1: composição/defaultPrevented → campo (exceto Ctrl/⌘+K) → modificadores (exceto Ctrl/⌘+K) →
 * repetição (exceto zoom) → TV → tecla por `event.key`.
 */
export function handleShortcut<O extends string>(e: KeyboardEvent, h: ShortcutHandlers<O>): boolean {
  if (e.defaultPrevented || e.isComposing || e.keyCode === 229) return false;
  const u = ui.value;
  const key = e.key ?? '';
  // Ctrl/⌘+K: alterna a busca; vale dentro de campos; AltGr (Ctrl+Alt) não conta; `code` cobre layouts não latinos.
  if ((e.ctrlKey || e.metaKey) && !e.altKey && (key.toLowerCase() === 'k' || e.code === 'KeyK')) {
    if (e.repeat || u.tv.on || u.aboutOpen !== null) return false;
    h.toggleSearch();
    e.preventDefault();
    return true;
  }
  if (e.altKey || e.ctrlKey || e.metaKey) return false;
  if (isTypingTarget(e.target) || inSliderOrListbox(e.target)) return false;
  const zoomKey = key === '+' || key === '=' || key === '-' || key === '_';
  if (e.repeat && !zoomKey) return false;
  if (u.tv.on) return false;
  // Daqui para baixo, só atalhos de uma tecla (chave X1) e só com busca, Sobre e seletor fechados.
  if (!h.oneKeyEnabled()) return false;
  if (u.searchOpen || u.aboutOpen !== null || u.pickerOpen) return false;
  let acted = false;
  switch (key) {
    case '/':
      h.toggleSearch(true);
      acted = true;
      break;
    case '1':
    case '2':
    case '3':
    case '4': {
      const list = h.offices();
      const office = list.length >= 2 ? list[Number(key) - 1] : undefined;
      if (office !== undefined) {
        h.setOffice(office);
        acted = true;
      }
      break;
    }
    case '+':
    case '=':
      acted = h.zoom?.('in') === true;
      break;
    case '-':
    case '_':
      acted = h.zoom?.('out') === true;
      break;
    case '0':
      acted = h.zoom?.('reset') === true;
      break;
    case 'c':
    case 'C':
      acted = h.toggleScreen?.() === true;
      break;
  }
  if (acted) e.preventDefault();
  return acted;
}

let uninstallCurrent: (() => void) | null = null;

/** Instala o ouvinte único (bolha em `window`). Chamar de novo reinstala. Devolve o desligamento. */
export function installShortcuts<O extends string>(h: ShortcutHandlers<O>, win: Window = window): () => void {
  uninstallCurrent?.();
  const onKeyDown = (e: KeyboardEvent) => {
    handleShortcut(e, h);
  };
  win.addEventListener('keydown', onKeyDown);
  const uninstall = () => {
    win.removeEventListener('keydown', onKeyDown);
    if (uninstallCurrent === uninstall) uninstallCurrent = null;
  };
  uninstallCurrent = uninstall;
  return uninstall;
}
